import { apiClient } from './client'
import type { ApiResponse, ProfileDTO } from '../types'

interface PresignedUrlResponse {
  uploadUrl: string
  downloadUrl: string
  objectKey: string
  expiresInSeconds: number
}

export const profileApi = {
  createProfile: (data: Partial<ProfileDTO>) =>
    apiClient.post<ApiResponse<ProfileDTO>>('/profiles', data),

  getMyProfile: () =>
    apiClient.get<ApiResponse<ProfileDTO>>('/profiles/me'),

  // Lost-profile members (2026-09 migration): name/gender we still know; 204 for everyone else
  prefill: () =>
    apiClient.get<ApiResponse<{ fullName?: string; gender?: string; restored?: boolean }>>('/profiles/prefill'),

  updateProfile: (data: Partial<ProfileDTO>) =>
    apiClient.patch<ApiResponse<ProfileDTO>>('/profiles/me', data),

  getProfile: (id: string) =>
    apiClient.get<ApiResponse<ProfileDTO>>(`/profiles/${id}`),

  avatarPresignedUrl: (contentType?: string) =>
    apiClient.post<ApiResponse<PresignedUrlResponse>>('/profiles/me/avatar/presigned-url', null, {
      params: contentType ? { contentType } : {},
    }),

  galleryPresignedUrl: (contentType?: string) =>
    apiClient.post<ApiResponse<PresignedUrlResponse>>('/profiles/me/gallery/presigned-url', null, {
      params: contentType ? { contentType } : {},
    }),

  addGalleryPhoto: (photoUrl: string) =>
    apiClient.post<ApiResponse<ProfileDTO>>('/profiles/me/gallery', { photoUrl }),

  removeGalleryPhoto: (index: number) =>
    apiClient.delete<ApiResponse<ProfileDTO>>(`/profiles/me/gallery/${index}`),

  removeAvatar: () =>
    apiClient.delete<ApiResponse<ProfileDTO>>('/profiles/me/avatar'),

  govtIdUploadUrl: () =>
    apiClient.post<ApiResponse<{ uploadUrl: string }>>('/profiles/me/govt-id/upload-url'),

  // Biodata auto-fill: server (Claude vision) reads the uploaded image and returns
  // profile fields; the image is deleted server-side right after extraction.
  biodataUploadUrl: () =>
    apiClient.post<ApiResponse<{ uploadUrl: string }>>('/profiles/me/biodata/upload-url'),

  extractBiodata: () =>
    apiClient.post<ApiResponse<Partial<ProfileDTO> & { gotraName?: string | null; motherGotraName?: string | null }>>(
      '/profiles/me/biodata/extract'),

  submitGovtId: (idType: string) =>
    apiClient.post<ApiResponse<void>>('/profiles/me/govt-id', { idType }),

  updateVisibility: (visibility: string) =>
    apiClient.patch<ApiResponse<string>>('/profiles/me/visibility', { visibility }),

  getViewers: () =>
    apiClient.get<ApiResponse<import('../types').ProfileDTO[]>>('/profiles/me/views'),

  blockProfile: (profileId: string) =>
    apiClient.post<ApiResponse<string>>(`/profiles/${profileId}/block`),

  recordView: (profileId: string) =>
    apiClient.post<ApiResponse<string>>(`/profiles/${profileId}/view`).catch(() => {}),
}
